/**
 * #2104 — Split Time at checkout.
 *
 * The visit span [check-in, check-out] is the single source of truth; task
 * times are DURATIONS that must sum to it. Submitted rows get sequential
 * synthetic ranges in original work order — overlap is impossible by
 * construction, and the parts always sum exactly to the whole.
 */

/**
 * Even split at whole-minute granularity, remainder minutes to the FIRST
 * rows (56m / 4 -> [14,14,14,14]; 58m / 4 -> [15,15,14,14]). Timecards
 * display minutes; per-row times must visibly sum to the total (#1996).
 */
export function evenSplitMinutes(totalMinutes: number, n: number): number[] {
  if (n <= 0) return [];
  const total = Math.max(0, Math.round(totalMinutes));
  const base = Math.floor(total / n);
  const remainder = total - base * n;
  return Array.from({ length: n }, (_, i) => base + (i < remainder ? 1 : 0));
}

export interface AllocSession {
  /** Stable key back to the IDB record (uuid). */
  key: string;
  projectId: number;
  /** Actual recorded seconds — drives proportional sub-split within a project. */
  actualSeconds: number;
}

export interface ProjectAllocation {
  projectId: number;
  minutes: number;
}

export interface PartitionedSession {
  key: string;
  stcalltime: string; // ISO, same format as the bulk path's session times
  edcalltime: string;
}

/**
 * Partition the visit span across sessions according to per-PROJECT minute
 * allocations. A project's minutes sub-split across its sessions proportional
 * to their actual durations (all-zero -> equal), because pause/resume produces
 * one CallLog row per atomic session (#1897) and every row must land inside
 * the partition.
 *
 * Invariants (unit-tested): ranges are sequential in the given session order
 * (no overlap possible), every range is inside [visitStart, visitEnd], and the
 * last range ends EXACTLY at visitEnd (global remainder correction) provided
 * the allocations sum to the span.
 *
 * Returns null when the inputs can't form a valid partition (unparseable span,
 * a session's project missing from the allocations) — callers fall back to the
 * actual recorded times.
 */
export function partitionSessions(
  visitStartISO: string,
  visitEndISO: string,
  sessions: ReadonlyArray<AllocSession>,
  perProject: ReadonlyArray<ProjectAllocation>,
): PartitionedSession[] | null {
  const start = new Date(visitStartISO).getTime();
  const end = new Date(visitEndISO).getTime();
  if (isNaN(start) || isNaN(end) || end <= start || sessions.length === 0) return null;

  const allocByProject = new Map(perProject.map((p) => [p.projectId, Math.max(0, p.minutes) * 60_000]));
  if (sessions.some((s) => !allocByProject.has(s.projectId))) return null;

  // Per-session share: proportional to actual seconds within the project.
  const projectActualTotals = new Map<number, number>();
  for (const s of sessions) {
    projectActualTotals.set(s.projectId, (projectActualTotals.get(s.projectId) ?? 0) + Math.max(0, s.actualSeconds));
  }
  const projectSessionCounts = new Map<number, number>();
  for (const s of sessions) {
    projectSessionCounts.set(s.projectId, (projectSessionCounts.get(s.projectId) ?? 0) + 1);
  }

  const shares = sessions.map((s) => {
    const allocMs = allocByProject.get(s.projectId)!;
    const actualTotal = projectActualTotals.get(s.projectId)!;
    const weight = actualTotal > 0 ? Math.max(0, s.actualSeconds) / actualTotal : 1 / projectSessionCounts.get(s.projectId)!;
    return allocMs * weight;
  });

  // Lay out sequentially; absorb float drift so the LAST session ends exactly
  // where the allocations say the partition ends (== visitEnd when balanced).
  const allocatedTotal = shares.reduce((a, b) => a + b, 0);
  const result: PartitionedSession[] = [];
  let cursor = start;
  sessions.forEach((s, i) => {
    const isLast = i === sessions.length - 1;
    const rangeEnd = isLast ? start + Math.round(allocatedTotal) : cursor + shares[i];
    result.push({
      key: s.key,
      stcalltime: new Date(Math.round(cursor)).toISOString(),
      edcalltime: new Date(Math.round(rangeEnd)).toISOString(),
    });
    cursor = rangeEnd;
  });
  return result;
}
